if (!customElements.get('pdp-info-tabs')) {
  customElements.define(
    'pdp-info-tabs',
    class PdpInfoTabs extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('[data-tablist]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));
        this.scrollButtons = Array.from(this.querySelectorAll('[data-tab-scroll]'));
        this.clamp = this.querySelector('[data-clamp]');
        this.clampToggle = this.querySelector('[data-clamp-toggle]');

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);
        this.updateScrollButton = this.updateScrollButton.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onTabKeydown);
        });

        if (this.tablist && this.scrollButtons.length) {
          this.tablist.addEventListener('scroll', this.updateScrollButton, { passive: true });
          this.scrollButtons.forEach((button) => {
            const direction = button.dataset.tabScroll === 'prev' ? -1 : 1;
            button.addEventListener('click', () => {
              this.tablist.scrollBy({ left: direction * this.tablist.clientWidth * 0.6, behavior: 'smooth' });
            });
          });
        }

        if (this.clamp && this.clampToggle) {
          this.clampToggle.addEventListener('click', () => this.toggleClamp());
        }

        this.querySelectorAll('[data-scope-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', () => this.activateScopeItem(trigger));
        });

        this.initMounts();

        // Re-measure when the layout changes (font load, resize, content swapped in).
        this.resizeObserver = new ResizeObserver(() => {
          this.updateScrollButton();
          this.updateClamp();
        });
        if (this.tablist) this.resizeObserver.observe(this.tablist);
        if (this.clamp) this.resizeObserver.observe(this.clamp.firstElementChild || this.clamp);
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      /* ---------- Tabs ---------- */

      onTabClick(event) {
        this.activateTab(event.currentTarget);
      }

      onTabKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        let nextIndex = null;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = (index + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
            nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        const nextTab = this.tabs[nextIndex];
        this.activateTab(nextTab);
        nextTab.focus();
      }

      activateTab(tab) {
        if (!tab) return;
        const key = tab.dataset.tab;

        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== key;
        });

        this.scrollTabIntoView(tab);

        // Hidden panels report a height of 0, so measure once the panel is shown.
        if (key === 'description') this.updateClamp();
      }

      scrollTabIntoView(tab) {
        if (!this.tablist || this.tablist.scrollWidth <= this.tablist.clientWidth) return;
        const listRect = this.tablist.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const offset = tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2;
        this.tablist.scrollBy({ left: offset, behavior: 'smooth' });
      }

      // Each arrow is shown only while there is scrollable space in its direction.
      updateScrollButton() {
        if (!this.tablist || !this.scrollButtons.length) return;
        const maxScroll = this.tablist.scrollWidth - this.tablist.clientWidth;
        const scrollLeft = this.tablist.scrollLeft;

        this.scrollButtons.forEach((button) => {
          const canScroll = button.dataset.tabScroll === 'prev' ? scrollLeft > 1 : scrollLeft < maxScroll - 1;
          button.hidden = maxScroll <= 1 || !canScroll;
        });
      }

      /* ---------- Beschreibung: "Mehr anzeigen" ---------- */

      updateClamp() {
        if (!this.clamp || !this.clampToggle || this.clamp.offsetParent === null) return;
        if (this.clamp.classList.contains('is-expanded')) return;

        const overflowing = this.clamp.scrollHeight > this.clamp.clientHeight + 1;
        this.clamp.classList.toggle('is-overflowing', overflowing);
        this.clampToggle.hidden = !overflowing;
      }

      toggleClamp() {
        const expand = !this.clamp.classList.contains('is-expanded');
        const label = this.clampToggle.querySelector('[data-clamp-label]');

        this.clamp.classList.toggle('is-expanded', expand);
        this.clampToggle.setAttribute('aria-expanded', String(expand));
        if (label) {
          label.textContent = expand ? this.clampToggle.dataset.labelLess : this.clampToggle.dataset.labelMore;
        }

        if (!expand) {
          this.updateClamp();
          const panelTop = this.clamp.getBoundingClientRect().top;
          if (panelTop < 0) {
            this.clamp.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }
      }

      /* ---------- Lieferumfang ---------- */

      activateScopeItem(trigger) {
        const index = trigger.dataset.scopeTrigger;

        this.querySelectorAll('[data-scope-trigger]').forEach((item) => {
          const active = item.dataset.scopeTrigger === index;
          const content = document.getElementById(item.getAttribute('aria-controls'));
          item.setAttribute('aria-expanded', String(active));
          item.closest('[data-scope-item]').classList.toggle('is-active', active);
          if (content) content.hidden = !active;
        });

        this.querySelectorAll('[data-scope-specs]').forEach((card) => {
          card.hidden = card.dataset.scopeSpecs !== index;
        });
      }

      /* ---------- Halterungen ---------- */

      // The mounts content is maintained as HTML in a global modal block (sections/modals.liquid).
      // Copy the rendered modal into the panel and restructure it into the tab design.
      // The HTML nests headings at different depths, so it is flattened in document order:
      // every heading starts a new mount, everything until the next heading belongs to it.
      initMounts() {
        const panel = this.querySelector('[data-mounts-source]');
        if (!panel) return;

        const source = document.getElementById(panel.dataset.mountsSource);
        const sourceContent = source && source.querySelector('.popup-modal__content-info');
        if (!sourceContent) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initMounts(), { once: true });
          } else {
            this.removeTab('mounts');
          }
          return;
        }

        const content = sourceContent.cloneNode(true);
        const headline = content.querySelector('.headline');
        const headingText = panel.dataset.mountsHeading || (headline ? this.normalizeText(headline.textContent) : '');
        if (headline) headline.remove();
        // Modal-only call to action button.
        content.querySelectorAll(':scope > a.button').forEach((element) => element.remove());
        content.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        const items = [];
        this.collectMountItems(content, items);

        const intro = [];
        const mounts = [];
        items.forEach((item) => {
          if (item.type === 'heading') {
            mounts.push({ title: item.text, label: item.label, items: [] });
          } else {
            (mounts.length ? mounts[mounts.length - 1].items : intro).push(item);
          }
        });

        if (mounts.length === 0 && intro.length === 0) {
          this.removeTab('mounts');
          return;
        }

        this.hintIconTemplate = panel.querySelector('[data-mounts-hint-icon]');
        const fragment = document.createDocumentFragment();

        if (headingText) {
          fragment.appendChild(this.createElement('h3', 'pdp-info-tabs__panel-title', headingText));
        }
        if (intro.length) {
          const introWrapper = this.createElement('div', 'pdp-info-tabs__mount');
          this.renderMountItems(introWrapper, intro);
          fragment.appendChild(introWrapper);
        }

        if (mounts.length > 1) {
          // Desktop: pill buttons. Mobile: a native select styled as a pill (Figma 7498:902).
          const chips = this.createElement('div', 'pdp-info-tabs__chips');
          chips.setAttribute('role', 'group');
          if (headingText) chips.setAttribute('aria-label', headingText);

          const selectWrapper = this.createElement('div', 'pdp-info-tabs__select');
          const select = this.createElement('select', 'pdp-info-tabs__select-input');
          select.dataset.mountSelect = '';
          select.setAttribute('aria-label', headingText || 'Halterung auswählen');
          select.addEventListener('change', () => this.activateMount(select.value));
          selectWrapper.appendChild(select);
          const selectIcon = panel.querySelector('[data-mounts-select-icon]');
          if (selectIcon) selectWrapper.appendChild(selectIcon.content.cloneNode(true));

          mounts.forEach((mount, index) => {
            const mountId = `PdpInfoMount-${this.dataset.section}-${index + 1}`;
            // A `data-label` on the heading wins; otherwise "Gitterbalkon (90° oder 80°-60° Winkel)" -> "Gitterbalkon".
            const label = mount.label || mount.title.split(/\s\(|:/)[0].trim() || mount.title;
            const chip = this.createElement('button', 'pdp-info-tabs__chip', label);
            chip.type = 'button';
            chip.dataset.mountTrigger = mountId;
            chip.setAttribute('aria-controls', mountId);
            chip.setAttribute('aria-pressed', String(index === 0));
            chip.addEventListener('click', () => this.activateMount(mountId));
            chips.appendChild(chip);

            const option = this.createElement('option', '', label);
            option.value = mountId;
            select.appendChild(option);
          });

          fragment.appendChild(chips);
          fragment.appendChild(selectWrapper);
        }

        mounts.forEach((mount, index) => {
          const mountId = `PdpInfoMount-${this.dataset.section}-${index + 1}`;
          const wrapper = this.createElement('div', 'pdp-info-tabs__mount');
          wrapper.id = mountId;
          wrapper.dataset.mountPanel = mountId;
          wrapper.hidden = index !== 0;
          wrapper.appendChild(this.createElement('h4', 'pdp-info-tabs__panel-title', mount.title));
          this.renderMountItems(wrapper, mount.items);
          fragment.appendChild(wrapper);
        });

        panel.appendChild(fragment);
      }

      // Flattens the modal HTML into a list of heading / text / hint / card items.
      collectMountItems(node, items) {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            if (child.textContent.trim() !== '') items.push({ type: 'text', element: child });
            return;
          }
          if (child.nodeType !== Node.ELEMENT_NODE) return;

          if (/^H[1-6]$/.test(child.tagName)) {
            const text = this.normalizeText(child.textContent);
            if (text) items.push({ type: 'heading', text, label: this.normalizeText(child.dataset.label || '') });
            return;
          }

          if (child.tagName === 'IMG') {
            items.push({ type: 'card', image: child, caption: '' });
            return;
          }

          const hasHeading = child.querySelector('h1, h2, h3, h4, h5, h6') !== null;
          const imageCount = child.querySelectorAll('img').length;

          if (hasHeading || imageCount > 1) {
            this.collectMountItems(child, items);
            // Keep images of separate containers in separate rows.
            if (imageCount > 1) items.push({ type: 'break' });
            return;
          }

          if (imageCount === 1) {
            const image = child.querySelector('img');
            image.remove();
            items.push({ type: 'card', image, caption: this.normalizeText(child.textContent) });
            return;
          }

          const text = this.normalizeText(child.textContent);
          if (text === '') return;
          items.push({ type: /^hinweis\b/i.test(text) ? 'hint' : 'text', element: child });
        });
      }

      renderMountItems(wrapper, items) {
        let richtext = null;
        let cards = null;

        items.forEach((item) => {
          if (item.type !== 'text') richtext = null;
          if (item.type !== 'card') cards = null;

          switch (item.type) {
            case 'text': {
              if (!richtext) {
                richtext = this.createElement('div', 'pdp-info-tabs__richtext rte');
                wrapper.appendChild(richtext);
              }
              richtext.appendChild(this.toTextBlock(item.element));
              break;
            }
            case 'hint': {
              const hint = this.createElement('div', 'pdp-info-tabs__hint');
              if (this.hintIconTemplate) hint.appendChild(this.hintIconTemplate.content.cloneNode(true));
              const hintText = this.createElement('div', 'pdp-info-tabs__hint-text pdp-info-tabs__richtext');
              const text = this.normalizeText(item.element.textContent);
              const match = text.match(/^(hinweis:?)\s*([\s\S]*)$/i);

              if (item.element.children.length === 0 && match) {
                const paragraph = document.createElement('p');
                paragraph.appendChild(this.createElement('strong', '', match[1]));
                paragraph.append(` ${match[2]}`);
                hintText.appendChild(paragraph);
              } else {
                this.stripInlineStyles(item.element);
                hintText.append(...item.element.childNodes);
              }

              hint.appendChild(hintText);
              wrapper.appendChild(hint);
              break;
            }
            case 'card': {
              if (!cards) {
                cards = this.createElement('div', 'pdp-info-tabs__cards');
                wrapper.appendChild(cards);
              }
              const card = this.createElement('figure', 'pdp-info-tabs__card');
              if (item.caption) {
                card.appendChild(this.createElement('figcaption', 'pdp-info-tabs__card-caption', item.caption));
              }
              item.image.removeAttribute('style');
              item.image.classList.add('pdp-info-tabs__card-image');
              card.appendChild(item.image);
              cards.appendChild(card);
              break;
            }
          }
        });
      }

      // Converts a loose text node / inline element / container into a block for the rich text wrapper.
      toTextBlock(element) {
        if (element.nodeType === Node.TEXT_NODE) {
          return this.createElement('p', '', this.normalizeText(element.textContent));
        }

        const isBold = /font-weight:\s*(bold|[6-9]00)/i.test(element.getAttribute('style') || '');
        element.removeAttribute('style');
        this.stripInlineStyles(element);

        if (['UL', 'OL', 'P'].includes(element.tagName)) return element;

        const block = document.createDocumentFragment();
        if (['DIV', 'SECTION', 'ARTICLE'].includes(element.tagName)) {
          block.append(...element.childNodes);
          return block;
        }

        const paragraph = document.createElement('p');
        if (isBold) {
          const strong = document.createElement('strong');
          strong.append(...element.childNodes);
          paragraph.appendChild(strong);
        } else {
          paragraph.append(...element.childNodes);
        }
        return paragraph;
      }

      activateMount(mountId) {
        this.querySelectorAll('[data-mount-trigger]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.mountTrigger === mountId));
        });

        this.querySelectorAll('[data-mount-select]').forEach((select) => {
          select.value = mountId;
        });

        this.querySelectorAll('[data-mount-panel]').forEach((mount) => {
          mount.hidden = mount.dataset.mountPanel !== mountId;
        });
      }

      removeTab(key) {
        const tab = this.tabs.find((item) => item.dataset.tab === key);
        const panel = this.panels.find((item) => item.dataset.panel === key);
        const wasSelected = tab && tab.getAttribute('aria-selected') === 'true';

        if (tab) tab.remove();
        if (panel) panel.remove();
        this.tabs = this.tabs.filter((item) => item !== tab);
        this.panels = this.panels.filter((item) => item !== panel);

        if (this.tabs.length === 0) {
          this.hidden = true;
        } else if (wasSelected) {
          this.activateTab(this.tabs[0]);
        }
        this.updateScrollButton();
      }

      stripInlineStyles(root) {
        root.querySelectorAll('[style]').forEach((element) => element.removeAttribute('style'));
      }

      normalizeText(text) {
        return text.replace(/\s+/g, ' ').trim();
      }

      createElement(tagName, className, text) {
        const element = document.createElement(tagName);
        if (className) element.className = className;
        if (text) element.textContent = text;
        return element;
      }
    }
  );
}
